/**
 * Rola até o painel de notificações do Início (D3, desenho do portal 0.6): o "Ver todas" do sino leva a `/portal/inicio#notificacoes`.
 * ⚠️ Não é `id` + âncora do navegador: o Início mantém as DUAS variantes (compacta e coluna) no DOM ao mesmo tempo, uma escondida por CSS
 * (`xl:hidden`); `id` repetido é inválido e a âncora acharia a primeira, que a partir de `xl` está escondida. Cada seção leva
 * `data-alvo-do-hash="notificacoes"` e rola-se até a que está visível (`getClientRects()` vazio = escondida por um ancestral).
 */
export const ALVO_DO_HASH_DAS_NOTIFICACOES = "notificacoes";

/** A seção das notificações que a pessoa enxerga (a compacta ou a coluna), ou a primeira se nenhuma for medível. */
function acharAlvo(raiz: ParentNode): HTMLElement | null {
  const alvos = Array.from(
    raiz.querySelectorAll<HTMLElement>(`[data-alvo-do-hash="${ALVO_DO_HASH_DAS_NOTIFICACOES}"]`)
  );
  return alvos.find((el) => el.getClientRects().length > 0) ?? alvos[0] ?? null;
}

export function rolarAteAsNotificacoes(raiz: ParentNode = document): boolean {
  const alvo = acharAlvo(raiz);
  if (!alvo) return false;
  alvo.scrollIntoView({ behavior: "smooth", block: "start" });
  return true;
}

/** Por quanto tempo a rolagem insiste enquanto a página cresce (depois disso, a página assentou ou a pessoa já está lendo). */
export const PRAZO_DA_INSISTENCIA_MS = 3000;

// O que a pessoa faz quando assume a rolagem. A rolagem suave do próprio `scrollIntoView` não dispara nenhum destes.
const GESTOS_DA_PESSOA = ["wheel", "touchstart", "touchmove", "pointerdown", "keydown"] as const;

/**
 * Os contêineres que rolam por onde a seção passa (o `main` do PortalLayout) ou, sem nenhum, a página. TODOS os ancestrais com `overflow-y`, não só o
 * mais próximo: `overflow-x: hidden` também resolve o `overflow-y` para `auto`, e um ancestral assim, sem rolagem de verdade, esconderia o `main`.
 */
function acharFaixasRolaveis(el: HTMLElement): HTMLElement[] {
  const faixas: HTMLElement[] = [];
  for (let pai = el.parentElement; pai; pai = pai.parentElement) {
    const { overflowY } = getComputedStyle(pai);
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") faixas.push(pai);
  }
  return faixas.length > 0
    ? faixas
    : [(document.scrollingElement as HTMLElement | null) ?? document.documentElement];
}

/**
 * Rola até as notificações NA HORA e continua tentando enquanto a página cresce. 🔴 Chegando com o Início frio (recarregar em outra tela e
 * tocar "Ver todas" no sino), o 1º `scrollIntoView` roda quando o feed e os comunicados ainda não existem: a faixa rolável tem 0 px e a
 * rolagem não tem para onde ir; 760 ms depois ela passa a 1.741 px e nada rola (E2E D-2, medido a 390×844).
 *
 * Um `ResizeObserver` no conteúdo da faixa rolável refaz a rolagem quando a altura rolável (`scrollHeight`) muda — comparar a altura filtra a
 * 1ª entrega do observador, que vem sempre. Para no PRIMEIRO gesto da pessoa (rolar, tocar, teclar: ela assumiu, não se briga com ela), no
 * fim do prazo curto ou no cancelamento devolvido (sair da página, outro "Ver todas"). Sem `ResizeObserver`, rola uma vez só.
 *
 * Escolhida entre refazer a rolagem quando cada query do Início termina: o observador não depende de QUAL conteúdo cresceu (feed, imagens,
 * notificações, vídeo) nem de uma lista de queries que mudaria com a tela, e a parada no gesto resolve a briga com a pessoa.
 */
export function rolarAteAsNotificacoesInsistindo(
  raiz: ParentNode = document,
  prazoMs: number = PRAZO_DA_INSISTENCIA_MS
): () => void {
  const alvo = acharAlvo(raiz);
  if (!alvo) return () => {};
  rolarAteAsNotificacoes(raiz);
  if (typeof ResizeObserver === "undefined") return () => {};

  const faixas = acharFaixasRolaveis(alvo);
  const alturas = () => faixas.map((faixa) => faixa.scrollHeight).join();
  let ultimasAlturas = alturas();
  let encerrado = false;

  const observador = new ResizeObserver(() => {
    const agora = alturas();
    if (agora === ultimasAlturas) return;
    ultimasAlturas = agora;
    rolarAteAsNotificacoes(raiz);
  });
  // O contêiner tem altura fixa (`h-dvh`): quem cresce é o que está DENTRO dele (na página, o `body`).
  for (const faixa of faixas) {
    const naPagina = faixa === document.scrollingElement || faixa === document.documentElement;
    observador.observe(faixa);
    for (const filho of naPagina ? [document.body] : Array.from(faixa.children)) {
      observador.observe(filho);
    }
  }

  const encerrar = () => {
    if (encerrado) return;
    encerrado = true;
    clearTimeout(prazo);
    observador.disconnect();
    for (const gesto of GESTOS_DA_PESSOA) window.removeEventListener(gesto, encerrar, true);
  };
  for (const gesto of GESTOS_DA_PESSOA) {
    window.addEventListener(gesto, encerrar, { capture: true, passive: true });
  }
  const prazo = setTimeout(encerrar, prazoMs);
  return encerrar;
}
